/** @jsxImportSource @emotion/react */
import {
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverHeader,
  PopoverTrigger,
} from "@chakra-ui/react";
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import React, { useEffect, useState } from "react";
import ReactTooltip from "react-tooltip";
import { useRecoilState } from "recoil";
import { ZoomAtomCampaignNotes } from "../../../atoms/ZoomAtom";
import { customTooltip, notSelect } from "../../../styles/general";
import { Option } from "../../../types/SavedSearchTypes";
import { useBrowserAgent } from "../../../utils/BrowserAgent";
import { localStorageSetItem } from "../../../utils/LocalStorage";
import IconBerserkerMail from "../../Icon";
import StyledButton from "../../StyledButton";

const PopoverHeaderCSS = css`
  font-size: 14px;
  font-weight: 400;
  line-height: 26px;
  text-transform: uppercase;
  color: #707070;

  padding: 8px 24px 0;
`;

const Container = styled.div`
  width: calc(100% + 50px);

  padding: 17px 25px 0;

  display: flex;
  align-items: flex-end;
  justify-content: center;

  border-top: 1px solid;
  border-color: inherit;

  @media screen and (max-width: 1024px) {
    width: 100%;

    padding: 25px;
  }
`;

const ModalFooterContainer = styled.div`
  max-width: 1000px;
  width: 100%;

  display: flex;
  align-items: center;
  justify-content: space-between;

  .buttons-wrapper {
    display: flex;
    align-items: center;
    justify-content: center;
    grid-gap: 12px;

    > button {
      width: 96px;
    }
  }
`;

const OutlinedButton = styled.button<{ webkit: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  font-weight: 600;
  letter-spacing: 0px;
  padding: 10px 20px;
  background-color: white;
  color: #555555;
  border-style: none;
  cursor: pointer;
  transition: opacity 0.2s;
  margin: -25px 0px -32px -13px;
  height: 78px;
  transition: all 0.3s ease-in-out;
  border-top: ${(props) => (props.webkit ? "none" : "1px solid")};
  border-color: inherit;
  &:hover,
  &:active {
    &:not([disabled]) {
      background-color: #efefef;
    }
  }
`;

const TooltipText = styled.span`
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 0px;
  color: #ffffff;
`;

const PopoverButton = styled.div`
  width: 220px;
  height: 64px;

  padding: 0 12px;

  border-radius: 4px;

  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  grid-gap: 2px;

  transition: background-color 0.3s ease-in-out;

  cursor: pointer;

  &.disabled {
    opacity: 0.72;

    pointer-events: none;
  }

  &:not(.disabled):hover {
    background: #fb972e;
    color: white;

    .title {
      color: white;
    }

    .description {
      color: white;
    }
  }

  .title {
    font-size: 16px;
    font-weight: 600;
    color: #333333;

    transition: color 0.3s ease-in-out;
  }

  .description {
    font-size: 13px;
    font-weight: 400;
    color: #707070;

    transition: color 0.3s ease-in-out;
  }
`;

const zoomCss = css`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 78px;
  margin: -25px 0px -32px 14px;
`;

const ZOOMS: Option[] = [
  { name: "75%", id: "1" },
  { name: "100%", id: "2" },
  { name: "125%", id: "3" },
  { name: "150%", id: "4" },
  { name: "175%", id: "5" },
  { name: "200%", id: "6" },
  { name: "225%", id: "7" },
  { name: "250%", id: "8" },
  { name: "275%", id: "9" },
  { name: "300%", id: "10" },
];

const LessZoom = styled.div<{ disabled: boolean; webkit: boolean }>`
  ${zoomCss}
  font-size: 13px;
  font-weight: 400;
  background: ${(props) => (props.disabled ? "#dcdcdc" : "#fff")};
  cursor: ${(props) => (props.disabled ? "not-allowed" : "pointer")};
  color: ${(props) => (props.disabled ? "#7f7c7c" : "#555555")};
  width: 44px;
  border-top: ${(props) => (props.webkit ? "none" : "1px solid")};
  border-color: inherit;

  transition: all 0.3s ease-in-out;

  &:hover {
    background: #efefef;
  }
`;

const CurrentZoom = styled.div<{ disabled: boolean; webkit: boolean }>`
  ${zoomCss}
  margin-left: 0px !important;
  width: 72px;
  color: #555555;
  cursor: pointer;

  border-top: ${(props) => (props.webkit ? "none" : "1px solid")};
  border-color: inherit;

  transition: all 0.3s ease-in-out;

  &:hover {
    background: #efefef;
  }
`;

const MoreZoom = styled.div<{ disabled: boolean; webkit: boolean }>`
  ${zoomCss}
  margin-left: 0px !important;
  font-size: 18px;
  font-weight: 400;
  background: ${(props) => (props.disabled ? "#dcdcdc" : "#fff")};
  cursor: ${(props) => (props.disabled ? "not-allowed" : "pointer")};
  color: ${(props) => (props.disabled ? "#7f7c7c" : "#555555")};
  border-top: ${(props) => (props.webkit ? "none" : "1px solid")};
  border-color: inherit;
  width: 48px;

  transition: all 0.3s ease-in-out;

  &:hover {
    background: #efefef;
  }
`;

const TooltipStyles = css`
  width: fit-content !important;
  padding: 2.5px 8px !important;
  margin-top: -35px !important;
`;

interface NoteModalFooterProps {
  isLoading: boolean;
  hasNotesContent: boolean;
  onSave: () => void;
  onCancel: () => void;
  onCopyText: () => void;
  onCopyTaskItems: () => void;
}

export default function NoteModalFooter({
  isLoading,
  hasNotesContent,
  onSave,
  onCancel,
  onCopyText,
  onCopyTaskItems,
}: NoteModalFooterProps) {
  const { isWebKit } = useBrowserAgent();
  const [selectedZoom, setSelectedZoom] = useRecoilState(ZoomAtomCampaignNotes);
  const [minZoomReached, setMinZoomReached] = useState(false);
  const [maxZoomReached, setMaxZoomReached] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const open = () => setIsOpen(!isOpen);
  const close = () => setIsOpen(false);

  useEffect(() => {
    if (selectedZoom === ZOOMS[0]) setMinZoomReached(true);
    else if (selectedZoom === ZOOMS[9]) setMaxZoomReached(true);
    else {
      setMinZoomReached(false);
      setMaxZoomReached(false);
    }
  }, [selectedZoom]);

  function increaseZoom() {
    const index = ZOOMS.findIndex((elem) => elem.id === selectedZoom?.id);
    if (index < 9) {
      setSelectedZoom(ZOOMS[index + 1]);

      localStorageSetItem("CAMPAIGN_NOTE_ZOOM", index + 1 + "");
    }
  }

  function decreaseZoom() {
    const index = ZOOMS.findIndex((elem) => elem.id === selectedZoom?.id);
    if (index > 0) {
      setSelectedZoom(ZOOMS[index - 1]);

      localStorageSetItem("CAMPAIGN_NOTE_ZOOM", index - 1 + "");
    }
  }

  function normalZoom() {
    setSelectedZoom(ZOOMS[1]);
    localStorageSetItem("CAMPAIGN_NOTE_ZOOM", 1 + "");
  }

  return (
    <Container>
      <ModalFooterContainer>
        <div style={{ display: "flex", alignItems: "center" }}>
          <Popover
            returnFocusOnClose={false}
            isOpen={isOpen}
            onClose={close}
            placement="top-start"
          >
            <PopoverTrigger>
              <OutlinedButton onClick={open} webkit={isWebKit}>
                Copy Options
              </OutlinedButton>
            </PopoverTrigger>
            <PopoverContent width="fit-content">
              <PopoverHeader css={PopoverHeaderCSS}>Copy Options</PopoverHeader>
              <PopoverArrow />
              <PopoverBody>
                <PopoverButton
                  className={hasNotesContent ? " " : "disabled"}
                  onClick={onCopyText}
                >
                  <div className="title">Text copy</div>
                  <div className="description">Without formatting</div>
                </PopoverButton>

                <PopoverButton
                  className={hasNotesContent ? " " : "disabled"}
                  onClick={onCopyTaskItems}
                >
                  <div className="title">CSV copy</div>
                  <div className="description">
                    Creates columns for the checklist
                  </div>
                </PopoverButton>
              </PopoverBody>
            </PopoverContent>
          </Popover>

          <div data-tip data-for="ZoomOut">
            <LessZoom
              onClick={() => decreaseZoom()}
              disabled={minZoomReached}
              webkit={isWebKit}
            >
              <IconBerserkerMail name="less-zoom" />
            </LessZoom>
            <ReactTooltip
              id="ZoomOut"
              place="top"
              backgroundColor="#333333"
              css={css`
                ${customTooltip}
                ${TooltipStyles}
                margin-left: 7px !important;
              `}
              effect="solid"
              arrowColor="transparent"
            >
              <TooltipText>Zoom Out</TooltipText>
            </ReactTooltip>
          </div>

          <div data-tip data-for="ZoomZero">
            <CurrentZoom
              css={css`
                font-size: 15px;
                font-weight: 600;
                ${notSelect}
              `}
              onClick={() => normalZoom()}
              disabled
              webkit={isWebKit}
            >
              {selectedZoom.name}
            </CurrentZoom>
            <ReactTooltip
              id="ZoomZero"
              place="top"
              backgroundColor="#333333"
              css={css`
                ${customTooltip}
                ${TooltipStyles}
              `}
              effect="solid"
              arrowColor="transparent"
            >
              <TooltipText>Go to 100%</TooltipText>
            </ReactTooltip>
          </div>

          <div data-tip data-for="ZoomIn">
            <MoreZoom
              css={notSelect}
              onClick={() => increaseZoom()}
              disabled={maxZoomReached}
              webkit={isWebKit}
            >
              <IconBerserkerMail name="more-zoom" />
            </MoreZoom>
            <ReactTooltip
              id="ZoomIn"
              place="top"
              backgroundColor="#333333"
              css={css`
                ${customTooltip}
                ${TooltipStyles}
              `}
              effect="solid"
              arrowColor="transparent"
            >
              <TooltipText>Zoom In</TooltipText>
            </ReactTooltip>
          </div>
        </div>

        <div className="buttons-wrapper">
          <StyledButton
            onClick={onCancel}
            inverted
            css={css`
              margin-bottom: -8px;
            `}
          >
            Cancel
          </StyledButton>
          <StyledButton
            onClick={onSave}
            disable={isLoading}
            disabled={isLoading}
            isLoading={isLoading}
            css={css`
              margin-bottom: -8px;
            `}
          >
            Save
          </StyledButton>
        </div>
      </ModalFooterContainer>
    </Container>
  );
}
